// Composite blocks: article card and accordion (native <details>, no script needed on the site).
import { registerBlock } from '../core/registry.js';
import { safeUrl } from '../core/sanitize.js';

function cardImage(el) {
    return el.querySelector(':scope > figure img, :scope > img');
}

function cardTitle(el) {
    return el.querySelector(':scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6');
}

registerBlock({
    name: 'card',
    label: 'block.card',
    icon: 'cards',
    category: 'components',
    priority: 75,
    match: el => el.classList.contains('pflow-card'),
    create: ({ h, t }) => h('div', { class: 'pflow-card h-100' },
        h('figure', { class: 'figure image pflow-img w-100' }, h('img', { class: 'figure-img img-fluid rounded w-100', src: '', alt: '' })),
        h('h3', {}, t('placeholder.cardTitle')),
        h('p', {}, t('placeholder.cardText'))),
    prepare: el => {
        const title = cardTitle(el);
        if (title) title.setAttribute('data-pf-edit', 'inline');
        el.querySelectorAll(':scope > p, :scope > div:not(.pflow-button)').forEach(node => node.setAttribute('data-pf-edit', 'inline'));
        el.querySelectorAll(':scope > .pflow-button a, :scope > a.btn').forEach(node => node.setAttribute('data-pf-edit', 'plain'));
    },
    fields: [
        {
            type: 'media',
            label: 'field.image',
            mediaType: 'image',
            when: el => !!cardImage(el),
            get: el => cardImage(el).getAttribute('src') || '',
            set: (el, value, ctx, media) => {
                const img = cardImage(el);
                img.setAttribute('src', safeUrl(value, { image: true }));
                if (media && !img.getAttribute('alt') && (media.alt || media.label)) img.setAttribute('alt', media.alt || media.label);
            },
        },
        {
            type: 'text',
            label: 'field.alt',
            when: el => !!cardImage(el),
            get: el => cardImage(el).getAttribute('alt') || '',
            set: (el, value) => cardImage(el).setAttribute('alt', value),
        },
        {
            type: 'toggle',
            label: 'field.showImage',
            get: el => !!cardImage(el),
            set: (el, value, ctx) => {
                const figure = el.querySelector(':scope > figure');
                if (value && !figure) {
                    el.prepend(ctx.h('figure', { class: 'figure image pflow-img w-100' }, ctx.h('img', { class: 'figure-img img-fluid rounded w-100', src: '', alt: '' })));
                } else if (!value && figure) figure.remove();
            },
        },
        {
            type: 'segmented',
            label: 'field.titleLevel',
            when: el => !!cardTitle(el),
            options: ['h2', 'h3', 'h4', 'h5'].map(value => ({ value, label: value.toUpperCase() })),
            get: el => cardTitle(el).localName,
            set: (el, value, ctx) => ctx.retag(cardTitle(el), value, false),
        },
        {
            type: 'toggle',
            label: 'field.showButton',
            get: el => !!el.querySelector(':scope > .pflow-button'),
            set: (el, value, ctx) => {
                const button = el.querySelector(':scope > .pflow-button');
                if (value && !button) {
                    el.append(ctx.h('div', { class: 'pflow-button' },
                        ctx.h('a', { href: '#', class: 'btn btn-outline-primary btn-sm', 'data-pf-edit': 'plain' }, ctx.t('placeholder.readMore'))));
                } else if (!value && button) button.remove();
            },
        },
        { type: 'buttons', when: el => !!el.querySelector(':scope > .pflow-button'), target: el => el.querySelector(':scope > .pflow-button'), single: true },
    ],
});

// ---- Accordion ---------------------------------------------------------------------------------------

function accordionItems(el) {
    return Array.from(el.querySelectorAll(':scope > details'));
}

registerBlock({
    name: 'accordion',
    label: 'block.accordion',
    icon: 'caret-circle-down',
    category: 'components',
    priority: 75,
    match: el => el.classList.contains('pflow-accordion'),
    create: ({ h, t }) => h('div', { class: 'pflow-accordion' },
        [1, 2, 3].map(n => h('details', { class: 'border rounded-3 mb-2 px-3 py-2', open: n === 1 ? '' : undefined },
            h('summary', { class: 'fw-semibold py-1' }, `${t('placeholder.question')} ${n}`),
            h('div', { class: 'pt-2' }, h('p', {}, t('placeholder.answer')))))),
    // Every item stays open while editing; the state chosen for the site is restored at export.
    prepare: el => {
        accordionItems(el).forEach(details => {
            if (!details.hasAttribute('data-pf-open')) details.setAttribute('data-pf-open', details.hasAttribute('open') ? '1' : '0');
            details.setAttribute('open', '');
            const summary = details.querySelector(':scope > summary');
            if (summary) summary.setAttribute('data-pf-edit', 'inline');
            const body = details.querySelector(':scope > div');
            if (body) body.setAttribute('data-pf-edit', 'rich');
        });
    },
    exportNode: el => {
        accordionItems(el).forEach(details => {
            const open = details.getAttribute('data-pf-open');
            details.removeAttribute('data-pf-open');
            if (open === '0') details.removeAttribute('open');
        });
        return el;
    },
    fields: [
        { type: 'accordion' },
        {
            type: 'toggle',
            label: 'field.firstOpen',
            get: el => (accordionItems(el)[0] || { getAttribute: () => '0' }).getAttribute('data-pf-open') === '1',
            set: (el, value) => accordionItems(el).forEach((details, index) => details.setAttribute('data-pf-open', index === 0 && value ? '1' : '0')),
        },
    ],
});

export { accordionItems };
